import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Car, FileText, Loader2, Map, Receipt, Route, Search, User } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Workspace } from "../lib/api";

const ICONS = { employee: User, vehicle: Car, contract: FileText, invoice: Receipt, route: Route, template: Map };
const GROUPS = { employee: "Pracownicy", vehicle: "Flota", contract: "Umowy", invoice: "Faktury", route: "Trasy", template: "Szablony tras" };

export const QuickSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  useEffect(() => { if (open) { setQ(""); setResults([]); } }, [open]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setLoading(false); return undefined; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => Workspace.search(q.trim())
      .then((r) => { if (alive) { setResults(r.results); setActive(0); } })
      .catch(() => alive && setResults([]))
      .finally(() => alive && setLoading(false)), 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  const go = (r) => { onClose(); navigate(r.link); };
  const onKeyDown = (e) => {
    if (!results.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((active + 1) % results.length); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((active - 1 + results.length) % results.length); }
    if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="quick-search-dialog" className="z-[1300] top-[12%] translate-y-0 max-w-xl w-[calc(100%-16px)] p-0 gap-0 rounded-2xl overflow-hidden" overlayClassName="z-[1290] bg-black/50">
        <DialogTitle className="sr-only">Szybkie wyszukiwanie</DialogTitle>
        <DialogDescription className="sr-only">Szukaj pracowników, aut, umów, faktur i tras</DialogDescription>
        <div className="flex items-center gap-3 px-4 border-b border-zinc-200">
          <Search className="w-5 h-5 text-zinc-400 shrink-0" />
          <input autoFocus data-testid="quick-search-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} placeholder="Kierowca, rejestracja, faktura, umowa, trasa…" className="flex-1 h-14 bg-transparent text-base outline-none" />
          {loading && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
        </div>
        <ul data-testid="quick-search-results" className="stagger max-h-[60vh] overflow-y-auto p-2">
          {q.trim().length < 2 && <li className="px-3 py-6 text-center text-sm text-zinc-500">Wpisz co najmniej 2 znaki. Skrót: Ctrl+K</li>}
          {q.trim().length >= 2 && !loading && !results.length && <li data-testid="quick-search-empty" className="px-3 py-6 text-center text-sm text-zinc-500">Brak wyników</li>}
          {results.map((r, i) => {
            const Icon = ICONS[r.kind] || Search;
            return (
              <li key={`${r.kind}-${r.id}-${i}`}>
                <button type="button" data-testid={`quick-search-result-${i}`} onMouseEnter={() => setActive(i)} onClick={() => go(r)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${i === active ? "bg-zinc-900 text-white" : "hover:bg-zinc-100"}`}>
                  <Icon className="w-4 h-4 shrink-0 opacity-70" />
                  <span className="min-w-0 flex-1"><span className="block text-sm font-semibold truncate">{r.title}</span><span className="block text-[11px] opacity-60 truncate">{r.subtitle}</span></span>
                  <span className="text-[10px] font-mono uppercase opacity-50">{GROUPS[r.kind]}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
